import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaNotEqual } from "react-icons/fa";
import {
  TextField,
  Alert,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  Slider,
} from "@mui/material";
import api from "./Api";
import { toast } from "react-toastify";
import { BarcodeFormat, BrowserMultiFormatReader, DecodeHintType } from "@zxing/library";
import { clearAuthSession, hasPermission } from "./auth";
import { getOrder, putOrders, addToQueue } from "./utils/scannerDB";
import { formatScanTime, getAppDateString, getAppDateTimeString } from "./utils/date";

import Header from "./scanner/Header";
import ScanInput from "./scanner/ScanInput";
import ScanProgress from "./scanner/ScanProgress";
import NsDialog from "./scanner/NsDialog";
import SkuImageLookupDialog from "./scanner/SkuImageLookupDialog";
import useOfflineScanner from "./scanner/useOfflineScanner";

const CANCEL_SCAN_OPTION = "CANCEL";

export default function BarcodeScanner() {
    const [selectedDate, setSelectedDate] = useState(() => {
    const todayStr = getAppDateString();
    const lastAutoDay = localStorage.getItem("last_auto_date_day");

    if (lastAutoDay === todayStr) {
      // Already auto-updated today → keep whatever date user set
      return localStorage.getItem("scan_date") || todayStr;
    }

    // First open of the day → set to today and mark this day as done
    localStorage.setItem("scan_date", todayStr);
    localStorage.setItem("last_auto_date_day", todayStr);
    return todayStr;
    });

    const today = selectedDate;
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState("");
    const canScanCancelPackets = hasPermission("cancel_packets");
    const [scannedTrackingId, setScannedTrackingId] = useState("");
    const [statusMessage, setStatusMessage] = useState({ type: "", message: "" });
    const [scanSummary, setScanSummary] = useState([]);
    const [lastSuccessScan, setLastSuccessScan] = useState({ id: "", account: "" });
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState("");
    const [showCamera, setShowCamera] = useState(false);
    const scanInputRef = useRef(null);
    const webcamRef = useRef(null);
    const codeReaderRef = useRef(null);
    const scanLockRef = useRef(false);
    const decodedScanRef = useRef({ id: "", time: 0 });
    const handleScanSubmitRef = useRef(null);
    const [cameraStatus, setCameraStatus] = useState("");
    const [cameraCapabilities, setCameraCapabilities] = useState({ zoom: null, torch: false, focus: false });
    const [cameraZoom, setCameraZoom] = useState(1);
    const [torchOn, setTorchOn] = useState(false);
    const [lastScanMeta, setLastScanMeta] = useState({ id: "", time: 0 });
    const DUPLICATE_IGNORE_MS = 3000; // 3 seconds silent duplicate window

    // ✅ COOLDOWN — configurable from Settings, saved in localStorage
    const [cooldownSeconds, setCooldownSeconds] = useState(() => {
        const saved = localStorage.getItem("cooldown_seconds");
        return saved ? parseInt(saved, 10) : 3;
    });
    const [cooldownActive, setCooldownActive] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState(0);
    const cooldownTimerRef = useRef(null);

    // ✅ State for N/S Dialog and Data
    const [nsDialogOpen, setNsDialogOpen] = useState(false);
    const [nsInitialGroup, setNsInitialGroup] = useState("");
    const [selectedNsAccount, setSelectedNsAccount] = useState("");
    const [nsOrderData, setNsOrderData] = useState([]);
    const [showAllAccounts, setShowAllAccounts] = useState(false);
    const [imageLookupOpen, setImageLookupOpen] = useState(false);

    const refocus = () =>
        setTimeout(() => scanInputRef.current?.focus(), 50);

    const {
    offlineReady,
    preloadProgress,
    pendingCount,
    syncStatus,
    preloadOfflineData,
    } = useOfflineScanner(selectedDate);
    const [settingsOpen, setSettingsOpen] = useState(false);

    // ✅ COOLDOWN START FUNCTION — uses configurable cooldownSeconds
    const startCooldown = useCallback(() => {
        if (cooldownSeconds <= 0) return; // 0 = cooldown disabled

        setCooldownActive(true);
        setCooldownRemaining(cooldownSeconds);

        if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);

        cooldownTimerRef.current = setInterval(() => {
            setCooldownRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownTimerRef.current);
                    cooldownTimerRef.current = null;
                    setCooldownActive(false);
                    setTimeout(() => scanInputRef.current?.focus(), 50);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [cooldownSeconds]);

    // Cleanup cooldown on unmount
    useEffect(() => {
        return () => {
            if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
        };
    }, []);

    // ✅ Save cooldown setting to localStorage
    useEffect(() => {
        localStorage.setItem("cooldown_seconds", cooldownSeconds.toString());
    }, [cooldownSeconds]);

    // ✅ Fetch Unscanned Orders
    const fetchUnscannedOrders = async (accountName) => {
        try {
            const endpoint =
                accountName === "all"
                    ? "/unscanned-orders/all"
                    : `/unscanned-orders/${encodeURIComponent(accountName.trim())}`;
    
            const response = await api.get(`/api${endpoint}`, {
            params: { date: selectedDate }
            });

    
            console.log(`✅ Response for ${accountName}:`, response.data);
    
            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                setNsOrderData(response.data);
            } else {
                console.warn(`⚠️ No unscanned orders found for account: ${accountName}`);
                setNsOrderData([]);
            }
        } catch (error) {
            console.error("❌ Error fetching unscanned orders:", error);
            alert("❌ Failed to fetch unscanned orders.");
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await api.get('/api/accounts?active_only=true');
            setAccounts(response.data);
        } catch (error) {
            console.error("❌ Error fetching accounts: ", error);
        }
    };
    

    const fetchScanSummary = async () => {
        try {
            const response = await api.get('/api/scan-summary', {
                params: { date: selectedDate }
            });
            setScanSummary(response.data);
        } catch (error) {
            console.error("❌ Error fetching scan summary:", error);
        }
    };
    
    const triggerAutoFocus = () => {
        if (webcamRef.current?.video) {
            const videoTrack = webcamRef.current.video.srcObject?.getVideoTracks()[0];
            
            if (videoTrack && videoTrack.getCapabilities) {
                const capabilities = videoTrack.getCapabilities();
    
                if (capabilities.focusDistance) {
                    try {
                        videoTrack.applyConstraints({
                            advanced: [{ focusMode: "continuous" }]
                        });
                        console.log("✅ Auto-focus triggered.");
                    } catch (err) {
                        console.warn("❌ Auto-focus not supported:", err);
                    }
                } else {
                    console.warn("⚠️ Auto-focus capability not available.");
                }
            } else {
                console.warn("⚠️ No video track or capabilities available.");
            }
        }
    };

    const getCameraTrack = useCallback(() => {
        return webcamRef.current?.video?.srcObject?.getVideoTracks?.()[0] || null;
    }, []);

    const readCameraCapabilities = useCallback(() => {
        const track = getCameraTrack();
        if (!track?.getCapabilities) return;
        const caps = track.getCapabilities();
        const zoom = caps.zoom ? {
            min: caps.zoom.min ?? 1,
            max: caps.zoom.max ?? 1,
            step: caps.zoom.step ?? 0.1,
        } : null;
        setCameraCapabilities({
            zoom,
            torch: Boolean(caps.torch),
            focus: Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous"),
        });
        if (zoom) {
            setCameraZoom((current) => Math.min(Math.max(current || zoom.min, zoom.min), Math.min(zoom.max, 3)));
        }
    }, [getCameraTrack]);

    const applyCameraSettings = useCallback(async (next = {}) => {
        const track = getCameraTrack();
        if (!track?.applyConstraints || !track.getCapabilities) return;
        const caps = track.getCapabilities();
        const advanced = {};
        const nextZoom = next.zoom ?? cameraZoom;
        const nextTorch = next.torch ?? torchOn;

        if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
            advanced.focusMode = "continuous";
        }
        if (caps.zoom && Number.isFinite(nextZoom)) {
            advanced.zoom = Math.min(Math.max(nextZoom, caps.zoom.min ?? 1), caps.zoom.max ?? nextZoom);
        }
        if (caps.torch) {
            advanced.torch = Boolean(nextTorch);
        }
        if (!Object.keys(advanced).length) return;

        try {
            await track.applyConstraints({ advanced: [advanced] });
        } catch (err) {
            console.warn("Camera constraints not applied:", err);
        }
    }, [cameraZoom, getCameraTrack, torchOn]);

    const handleCameraReady = useCallback(() => {
        readCameraCapabilities();
        applyCameraSettings();
        triggerAutoFocus();
    }, [applyCameraSettings, readCameraCapabilities]);

    useEffect(() => {
    fetchAccounts();
    }, []);

    useEffect(() => {
    console.log("🔁 [EFFECT] selectedDate changed:", selectedDate);

    fetchScanSummary();
    preloadOfflineData();

    }, [selectedDate]);


    useEffect(() => {
    localStorage.setItem("scan_date", selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        if (statusMessage.type) {
            setScannedTrackingId("");
        }
    }, [statusMessage]);

    useEffect(() => {
    if (selectedAccount) {
        setTimeout(() => scanInputRef.current?.focus(), 50);
    }
    }, [selectedAccount]);

    useEffect(() => {
    if (syncStatus === "complete") {
        console.log("🔄 [SYNC] complete → refreshing server summary");
        fetchScanSummary();
    }
    }, [syncStatus]);

    // ✅ Date only changes from Settings — no auto midnight change

    const playSound = (type) => {
    const map = {
        success: "/success.mp3",
        error: "/error.mp3",
        duplicate: "/duplicate.mp3",
    };
    new Audio(map[type] || map.error).play();
    };

    
    const stopCamera = useCallback(() => {
        codeReaderRef.current?.reset?.();
        codeReaderRef.current = null;
        const stream = webcamRef.current?.video?.srcObject;
        stream?.getTracks?.().forEach(track => track.stop());
        setCameraStatus("");
        setTorchOn(false);
    }, []);

    const toggleCamera = () => {
        if (showCamera) stopCamera();
        setShowCamera(!showCamera);
    };
    
    
    const handleScanSubmit = async (overrideTrackingId = null) => {
        const now = Date.now();
        const trackingId = (overrideTrackingId ?? scannedTrackingId).trim();
        const isCancelScan = selectedAccount === CANCEL_SCAN_OPTION;

        // ✅ COOLDOWN CHECK
        if (cooldownActive) {
            setStatusMessage({
                type: "warning",
                message: `⏳ Cooldown active. Wait ${cooldownRemaining}s before next scan.`,
            });
            setScannedTrackingId("");
            refocus();
            return;
        }

        // ✅ Ignore same barcode scanned too quickly after success
        if (
        trackingId === lastScanMeta.id &&
        now - lastScanMeta.time < DUPLICATE_IGNORE_MS
        ) {
        console.log("⏭ Silent duplicate ignored:", trackingId);
        setScannedTrackingId("");
        refocus();
        return;
        }

        if (!trackingId) {
            setStatusMessage({
                type: "error",
                message: `❌ Please select an account and enter a Tracking ID.`,
            });
            playSound("error");
            refocus();
            return;
        }

        if (isCancelScan) {
            if (!canScanCancelPackets) {
                setStatusMessage({
                    type: "error",
                    message: "❌ Cancel packet scan is not allowed for this login.",
                });
                playSound("error");
                setSelectedAccount("");
                refocus();
                return;
            }

            try {
                const response = await api.post("/api/cancel-scans", {
                    tracking_id: trackingId,
                    date: today,
                });

                if (response.data?.status === "duplicate") {
                    const scannedOn = response.data?.scan?.scanned_on || "earlier";
                    setStatusMessage({
                        type: "error",
                        message: `❌ Duplicate Cancel Packet: (${trackingId}) at ${scannedOn}`,
                    });
                    playSound("duplicate");
                    setScannedTrackingId("");
                    refocus();
                    return;
                }

                if (!response.data?.success) {
                    throw new Error(response.data?.message || "Cancel packet scan failed");
                }

                setLastScanMeta({ id: trackingId, time: now });
                setLastSuccessScan({
                    id: trackingId,
                    account: response.data?.scan?.account_name || "CANCEL",
                });
                setStatusMessage({
                    type: "success",
                    message: `✅ Cancel packet stored! (${trackingId})`,
                });
                playSound("success");
                startCooldown();
                setScannedTrackingId("");
                refocus();
            } catch (error) {
                console.error("❌ Cancel packet scan failed:", error);
                setStatusMessage({
                    type: "error",
                    message: error.response?.data?.message || `❌ Cancel packet scan failed. (${trackingId})`,
                });
                playSound("error");
                refocus();
            }
            return;
        }

        if (!offlineReady) {
        setStatusMessage({
            type: "error",
            message: preloadProgress === 0
            ? "⚠ Preparing offline data. Please wait..."
            : `⚠ Loading offline data (${preloadProgress}%). Please wait...`,
        });
        playSound("error");
        refocus();
        return;
        }

        try {
            // 🔍 LOCAL INDEXEDDB LOOKUP
            const order = await getOrder(trackingId);

            // ❌ NOT FOUND
            if (!order) {
                setStatusMessage({
                    type: "error",
                    message: `❌ Tracking ID Not Found. (${trackingId})`,
                });
                playSound("error");
                refocus();
                return;
            }

            // ❌ WRONG ACCOUNT
            if (
                selectedAccount &&
                selectedAccount !== "ALL" &&
                order.account !== selectedAccount
            ) {
                setStatusMessage({
                    type: "error",
                    message: `❌ Belongs to other account: ${order.account} (${trackingId})`,
                });
                setErrorDialogMessage(
                    `❌ Error: This Tracking ID belongs to another account: ${order.account}\n\nTracking ID: ${trackingId}`
                );
                setErrorDialogOpen(true);
                playSound("error");
                refocus();
                return;
            }

            // ❌ DUPLICATE (already scanned)
            if (order.scanned_on) {
            if (
                trackingId === lastScanMeta.id &&
                now - lastScanMeta.time < DUPLICATE_IGNORE_MS
            ) {
                console.log("⏭ Silent duplicate ignored:", trackingId);
                return;
            }

            const time24 = formatScanTime(order.scanned_on);

            setStatusMessage({
                type: "error",
                message: `❌ Duplicate: (${trackingId}) at ${time24}`,
            });
            playSound("duplicate");
            return;
            }


            // ✅ SUCCESS — ACCEPT LOCALLY (offline-first)
            const scannedOn = getAppDateTimeString();

            await putOrders([
                {
                    ...order,
                    scanned_on: scannedOn,
                },
            ]);

            const queueId = await addToQueue({
            tracking_id: trackingId,
            account: order.account,
            date: today,
            time: scannedOn,
            });

            console.log("🧾 [QUEUE] added with id", queueId);


            setLastScanMeta({ id: trackingId, time: now });
            setLastSuccessScan({
            id: trackingId,
            account: order.account,
            });
            setStatusMessage({
                type: "success",
                message: `✅ Scan! (${trackingId})`,
            });
            playSound("success");

            // ✅ START COOLDOWN after successful scan
            startCooldown();

            refocus();

        } catch (error) {
            console.error("❌ Local scan validation failed:", error);
            setStatusMessage({
                type: "error",
                message: `❌ Verification Failed. (${trackingId})`,
            });
            playSound("error");
        }
    };

    useEffect(() => {
        handleScanSubmitRef.current = handleScanSubmit;
    });

    useEffect(() => {
        if (!showCamera) {
            codeReaderRef.current?.reset?.();
            codeReaderRef.current = null;
            setCameraStatus("");
            return;
        }

        let cancelled = false;
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
            BarcodeFormat.CODE_128,
            BarcodeFormat.CODE_39,
            BarcodeFormat.CODE_93,
            BarcodeFormat.EAN_13,
            BarcodeFormat.EAN_8,
            BarcodeFormat.UPC_A,
            BarcodeFormat.UPC_E,
            BarcodeFormat.ITF,
            BarcodeFormat.CODABAR,
        ]);
        hints.set(DecodeHintType.TRY_HARDER, true);

        const reader = new BrowserMultiFormatReader(hints, 180);
        codeReaderRef.current = reader;
        setCameraStatus("Starting camera scanner...");

        const startDecoder = () => {
            if (cancelled) return;
            const video = webcamRef.current?.video;
            if (!video || video.readyState < 2) {
                window.setTimeout(startDecoder, 180);
                return;
            }

            readCameraCapabilities();
            applyCameraSettings();
            setCameraStatus("Scanning barcode...");

            reader.decodeFromVideoElementContinuously(video, (result) => {
                if (cancelled || !result) return;
                const trackingId = (result.getText?.() || "").trim();
                if (!trackingId) return;

                const now = Date.now();
                if (
                    scanLockRef.current ||
                    (trackingId === decodedScanRef.current.id && now - decodedScanRef.current.time < 1800)
                ) {
                    return;
                }

                scanLockRef.current = true;
                decodedScanRef.current = { id: trackingId, time: now };
                setScannedTrackingId(trackingId);
                setCameraStatus(`Scanned: ${trackingId}`);
                handleScanSubmitRef.current?.(trackingId);

                window.setTimeout(() => {
                    scanLockRef.current = false;
                }, Math.max(1200, cooldownSeconds * 1000));
            });
        };

        startDecoder();

        return () => {
            cancelled = true;
            reader.reset();
        };
    }, [applyCameraSettings, cooldownSeconds, readCameraCapabilities, showCamera]);

    useEffect(() => {
        if (showCamera) applyCameraSettings();
    }, [applyCameraSettings, showCamera]);

    useEffect(() => {
        return () => stopCamera();
    }, [stopCamera]);

    

return (
  <Box
    sx={{
      padding: { xs: "10px", md: "20px" },
      width: { xs: "100%", md: "80%" },
      margin: "0 auto",
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Header
    pendingCount={pendingCount}
    syncStatus={syncStatus}
    offlineReady={offlineReady}
    preloadProgress={preloadProgress}
    onOpenSettings={() => setSettingsOpen(true)}
    />

    {/* ⚙️ SETTINGS DIALOG — Contains Date Change + Logout (ONLY place to logout) */}
    <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
      <DialogTitle>Settings</DialogTitle>
      <DialogContent>
        <TextField
          label="Select Date"
          type="date"
          value={selectedDate}
          onChange={(e) => {
            if (pendingCount > 0 || syncStatus === "syncing") {
              toast.error("⏳ Sync in progress. Please wait.");
              return;
            }
            // ✅ No password required anymore
            setSelectedDate(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ mt: 1 }}
        />

        <Typography variant="body2" sx={{ mt: 1, color: "gray" }}>
          This date will be used for all scans and validations.
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* ✅ COOLDOWN SETTING */}
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          ⏱ Scan Cooldown: {cooldownSeconds}s
        </Typography>
        <Typography variant="body2" sx={{ color: "gray", mb: 1 }}>
          Wait time between scans. Set 0 to disable.
        </Typography>
        <Slider
          value={cooldownSeconds}
          onChange={(e, val) => setCooldownSeconds(val)}
          min={0}
          max={15}
          step={1}
          marks={[
            { value: 0, label: "Off" },
            { value: 3, label: "3s" },
            { value: 5, label: "5s" },
            { value: 10, label: "10s" },
            { value: 15, label: "15s" },
          ]}
          valueLabelDisplay="auto"
          sx={{ mx: 1 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setSettingsOpen(false)}>Close</Button>

        <Button
          color="error"
          onClick={async () => {
            try {
              await api.post("/api/auth/logout");
            } catch (error) {
              console.warn("Logout request failed:", error.message);
            }
            clearAuthSession();
            toast.success("✅ Logged out");
            setTimeout(() => (window.location.href = "/"), 800);
          }}
        >
          🔌 Logout
        </Button>
      </DialogActions>
    </Dialog>

    {/* ✅ COOLDOWN BAR */}
    {cooldownActive && (
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#fff3e0",
          border: "1px solid #ffb74d",
          borderRadius: 2,
          padding: "8px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: "bold", color: "#e65100", fontSize: "14px" }}>
          ⏳ Cooldown: {cooldownRemaining}s
        </Typography>
        <Box
          sx={{
            flex: 1,
            maxWidth: 200,
            height: 6,
            backgroundColor: "#ffe0b2",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${(cooldownRemaining / cooldownSeconds) * 100}%`,
              height: "100%",
              backgroundColor: "#ff9800",
              borderRadius: 3,
              transition: "width 1s linear",
            }}
          />
        </Box>
      </Box>
    )}

    {/* Error Dialog */}
    <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}>
      <DialogTitle>⚠️ Scan Error</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: "pre-line" }}>
          {errorDialogMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setErrorDialogOpen(false)}>OK</Button>
      </DialogActions>
    </Dialog>

    {/* Status Message */}
    {statusMessage.message && (
      <Alert severity={statusMessage.type === "warning" ? "warning" : statusMessage.type} sx={{ width: "100%" }}>
        {statusMessage.message}
      </Alert>
    )}

    
            <ScanInput
            accounts={accounts.filter(a =>
            (scanSummary.find(s => s.account_name === a.name)?.total_orders ?? 0) > 0
            )}
            selectedAccount={selectedAccount}
            setSelectedAccount={setSelectedAccount}
            canScanCancelPackets={canScanCancelPackets}
            cancelModeValue={CANCEL_SCAN_OPTION}
            scannedTrackingId={scannedTrackingId}
            setScannedTrackingId={setScannedTrackingId}
            toggleCamera={toggleCamera}
            showCamera={showCamera}
            webcamRef={webcamRef}
            triggerAutoFocus={triggerAutoFocus}
            onCameraReady={handleCameraReady}
            cameraStatus={cameraStatus}
            cameraCapabilities={cameraCapabilities}
            cameraZoom={cameraZoom}
            setCameraZoom={setCameraZoom}
            torchOn={torchOn}
            setTorchOn={setTorchOn}
            lastSuccessScan={lastSuccessScan}
            inputRef={scanInputRef}
            onSubmit={handleScanSubmit}
            cooldownActive={cooldownActive}
            />

            <ScanProgress
            accounts={accounts}
            scanSummary={scanSummary}
            onRefresh={fetchScanSummary}
            onOpenNotScanned={(group) => { setNsInitialGroup(group || ""); setNsDialogOpen(true); }}
            />
            <Button
            variant="contained"
            color="info"
            sx={{ mt: 1, fontWeight: "bold" }}
            onClick={() => setImageLookupOpen(true)}
            >
            IMAGE CHECK
            </Button>
            {/* N/S BUTTON */}
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FaNotEqual />}
            sx={{ mt: 2 }}
            onClick={() => { setNsInitialGroup(""); setNsDialogOpen(true); }}
            >
            SCAN
            </Button>

            {/* N/S Dialog */}
            <NsDialog
            open={nsDialogOpen}
            onClose={() => setNsDialogOpen(false)}
            initialGroup={nsInitialGroup}
            selectedDate={selectedDate}
            accounts={accounts.filter(a =>
            (scanSummary.find(s => s.account_name === a.name)?.total_orders ?? 0) > 0
            )}
            selectedNsAccount={selectedNsAccount}
            setSelectedNsAccount={setSelectedNsAccount}
            fetchUnscannedOrders={fetchUnscannedOrders}
            nsOrderData={nsOrderData}
            showAllAccounts={showAllAccounts}
            setShowAllAccounts={setShowAllAccounts}
            />

            <SkuImageLookupDialog
            open={imageLookupOpen}
            onClose={() => setImageLookupOpen(false)}
            selectedAccount={selectedAccount === "ALL" || selectedAccount === CANCEL_SCAN_OPTION ? "" : selectedAccount}
            />

        </Box>
    );
}
